import type {
  BrandBinding,
  CreativeTemplateExampleWritePayload,
  LayerContent,
  SceneGraph,
  SceneLayer,
} from '../types/creative-template-example.types';
import type { CreativeRole } from '../types/creative-template.types';
import { EMPTY_ZONE } from './creative-template.helpers';

export interface DemoBrand {
  slug: string;
  name: string;
  tagline: string;
  palette: Record<string, string>;
  typography: Record<string, string>;
  logo: string;
  assets: Record<string, string>;
}

export const DEMO_BRANDS: Record<string, DemoBrand> = {
  'demo-qsr': {
    slug: 'demo-qsr',
    name: 'Demo QSR',
    tagline: 'Taste the difference',
    palette: {
      primary: '#D62828',
      on_primary: '#FFFFFF',
      secondary: '#FCBF49',
      accent: '#003049',
      muted: '#6B7280',
    },
    typography: {
      heading: 'Georgia, serif',
      body: 'Helvetica, Arial, sans-serif',
    },
    logo: 'https://placehold.co/120x40/png?text=QSR+Logo',
    assets: {
      hero: 'https://placehold.co/400x300/png?text=Hero+Food',
    },
  },
  'demo-retail': {
    slug: 'demo-retail',
    name: 'Demo Retail',
    tagline: 'New season arrivals',
    palette: {
      primary: '#1D3557',
      on_primary: '#FFFFFF',
      secondary: '#F1FAEE',
      accent: '#E63946',
      muted: '#6B7280',
    },
    typography: {
      heading: 'Futura, sans-serif',
      body: 'Arial, sans-serif',
    },
    logo: 'https://placehold.co/120x40/png?text=Retail+Logo',
    assets: {
      hero: 'https://placehold.co/400x300/png?text=Retail+Hero',
    },
  },
  'demo-healthcare': {
    slug: 'demo-healthcare',
    name: 'Demo Healthcare',
    tagline: 'Your health matters',
    palette: {
      primary: '#2A9D8F',
      on_primary: '#FFFFFF',
      secondary: '#E9F5F3',
      accent: '#264653',
      muted: '#6B7280',
    },
    typography: {
      heading: 'Merriweather, serif',
      body: 'Open Sans, sans-serif',
    },
    logo: 'https://placehold.co/120x40/png?text=Health+Logo',
    assets: {
      hero: 'https://placehold.co/400x300/png?text=Healthcare',
    },
  },
};

export function resolveBrandToken(brand: DemoBrand, token: string): string {
  const parts = token.replace(/^brand\./, '').split('.');
  let current: unknown = brand;
  for (const part of parts) {
    if (!current || typeof current !== 'object') {
      return token;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : token;
}

export function applyBrandBindings(
  sceneGraph: SceneGraph,
  bindings: BrandBinding[],
  brandSlug: string,
): SceneGraph {
  const brand = DEMO_BRANDS[brandSlug] ?? DEMO_BRANDS['demo-qsr'];
  const layers = sceneGraph.layers.map((layer) => {
    const layerBindings = bindings.filter((binding) => binding.role === layer.role);
    if (!layerBindings.length) {
      return layer;
    }
    const content = { ...layer.content } as Record<string, unknown>;
    for (const binding of layerBindings) {
      content[binding.field] = resolveBrandToken(brand, binding.token);
    }
    return { ...layer, content: content as LayerContent };
  });
  return { ...sceneGraph, layers };
}

export function buildCreativeTemplateExamplePayload(
  values: CreativeTemplateExampleWritePayload,
): CreativeTemplateExampleWritePayload {
  return {
    ...values,
    example_id: values.example_id?.trim(),
    profile_id: values.profile_id?.trim(),
    thumbnail_url: values.thumbnail_url?.trim(),
    canonical_demo_brand_slug: values.canonical_demo_brand_slug?.trim(),
    change_note: values.change_note?.trim(),
  };
}

export function validateCreativeTemplateExampleForm(
  values: CreativeTemplateExampleWritePayload,
  options: { isCreate: boolean },
): string | null {
  if (options.isCreate && !values.example_id?.trim()) {
    return 'Example ID is required when creating an example.';
  }
  if (options.isCreate && !values.profile_id?.trim()) {
    return 'Profile ID is required when creating an example.';
  }
  if (options.isCreate && !values.size_key) {
    return 'Size key is required when creating an example.';
  }
  if (!values.scene_graph?.layers?.length) {
    return 'Scene graph must include at least one layer.';
  }
  return null;
}

export function parseCreativeTemplateExampleMutationError(error: unknown): string {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return 'Could not save template example. You may need staff or admin access.';
  }
  const data = (error as { data?: Record<string, unknown> }).data;
  if (!data) {
    return 'Could not save template example.';
  }
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  const messages = Object.entries(data)
    .map(([field, value]) => {
      if (Array.isArray(value)) {
        return `${field}: ${value.join(', ')}`;
      }
      return `${field}: ${String(value)}`;
    });
  return messages.length > 0 ? messages.join(' ') : 'Could not save template example.';
}

export function createDefaultLayer(role: CreativeRole): SceneLayer {
  if (role === 'background') {
    return {
      role,
      zone: { x: 0, y: 0, w: 1, h: 1 },
      content: { kind: 'fill', fill_color: '#E8E8E8' },
    };
  }
  if (role === 'hero' || role === 'logo') {
    return {
      role,
      zone: { ...EMPTY_ZONE },
      content: { kind: 'image', media_url: null, label: role === 'hero' ? 'Hero image' : 'Logo' },
    };
  }
  if (role === 'accent') {
    return {
      role,
      zone: { ...EMPTY_ZONE },
      content: { kind: 'shape', fill_color: '#FF6B00' },
    };
  }
  return {
    role,
    zone: { ...EMPTY_ZONE },
    content: {
      kind: 'text',
      content: role === 'headline' ? 'Headline' : role === 'cta' ? 'Learn more' : 'Copy',
      font_family: 'sans-serif',
      text_color: '#1A1A1A',
      font_size: role === 'legal' ? 10 : null,
    },
  };
}

export function exampleIdFor(profileId: string, sizeKey: string): string {
  return `${profileId}__${sizeKey}`;
}
